'use client'

import SignaturePad, { type SignaturePadHandle } from '@/components/signature/signature-pad'
import SignatureTyped from '@/components/signature/signature-typed'
import SignatureUpload from '@/components/signature/signature-upload'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { SIGNATURE_FONTS, signatureFontStack } from '@/constant/signature-fonts'
import { SIGNATURE_DISPLAY_CLASS } from '@/constant/signature-surface'
import { SignatureMethod } from '@/enum/global.enum'
import type { ICapturedSignature, ISigningContext } from '@/interface/signature.interface'
import { cn } from '@/lib/utils'
import { ArrowRight, Edit, PenLine, ShieldCheck, Type, Upload } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

/** What the capture component hands back once the signature is complete. */
export type CapturedSignatureResult = ICapturedSignature & {
  consentAccepted: boolean
  saveAsProfile: boolean
  useSavedSignature: boolean
}

export interface SignatureCaptureProps {
  signing: ISigningContext
  /** Emits null whenever the current input is not a usable signature. */
  onChange: (signature: CapturedSignatureResult | null) => void
  disabled?: boolean
  className?: string
}

const METHOD_TABS: { method: SignatureMethod; label: string; icon: typeof PenLine }[] = [
  { method: SignatureMethod.DRAWN, label: 'Draw', icon: PenLine },
  { method: SignatureMethod.TYPED, label: 'Type', icon: Type },
  { method: SignatureMethod.UPLOADED, label: 'Upload', icon: Upload },
]

/**
 * Signature capture: pick a method, produce a signature, accept the consent.
 *
 * Emits a signature only when it is genuinely complete - method-appropriate
 * content present *and* consent ticked - so the caller can bind the submit button
 * to a single non-null check rather than reimplementing the rules.
 */
const SignatureCapture = ({ signing, onChange, disabled = false, className }: SignatureCaptureProps) => {
  const padRef = useRef<SignaturePadHandle>(null)

  const allowed = useMemo(
    () => METHOD_TABS.filter((tab) => signing.allowedMethods.includes(tab.method)),
    [signing.allowedMethods]
  )

  const hasSaved = Boolean(signing.savedSignature)

  const [method, setMethod] = useState<SignatureMethod>(allowed[0]?.method ?? SignatureMethod.TYPED)
  const [useSaved, setUseSaved] = useState(hasSaved)
  const [drawnHasInk, setDrawnHasInk] = useState(false)
  const [typedText, setTypedText] = useState(signing.savedSignature?.typedText ?? signing.signerName ?? '')
  const [typedFont, setTypedFont] = useState(signing.savedSignature?.typedFont ?? SIGNATURE_FONTS[0].id)
  const [uploaded, setUploaded] = useState<string | null>(null)
  const [consentAccepted, setConsentAccepted] = useState(false)
  const [saveAsProfile, setSaveAsProfile] = useState(false)

  /**
   * Resolve the current input into a signature, or null when incomplete.
   *
   * The drawn branch reads the pad imperatively rather than holding the PNG in
   * state, which would mean re-encoding the canvas on every stroke.
   */
  const buildSignature = (): ICapturedSignature | null => {
    // The saved signature is never posted back as image data. The client only
    // holds a short-lived signed URL; the server reads the real image from the
    // profile when useSavedSignature is set.
    if (useSaved && signing.savedSignature) return { method: signing.savedSignature.method }

    if (method === SignatureMethod.DRAWN) {
      const dataUrl = padRef.current?.toDataUrl() ?? null
      return dataUrl ? { method: SignatureMethod.DRAWN, signatureImage: dataUrl } : null
    }

    if (method === SignatureMethod.TYPED)
      return typedText.trim() ? { method: SignatureMethod.TYPED, typedText: typedText.trim(), typedFont } : null

    if (method === SignatureMethod.UPLOADED)
      return uploaded ? { method: SignatureMethod.UPLOADED, signatureImage: uploaded } : null

    return null
  }

  // Re-emit whenever anything that affects completeness changes. drawnHasInk is
  // listed because the pad is read imperatively - the stroke count is what tells
  // us the exported PNG has changed.
  useEffect(() => {
    const signature = buildSignature()

    onChange(
      signature && consentAccepted
        ? { ...signature, consentAccepted, saveAsProfile: !useSaved && saveAsProfile, useSavedSignature: useSaved }
        : null
    )
  }, [method, useSaved, drawnHasInk, typedText, typedFont, uploaded, consentAccepted, saveAsProfile])

  const savedPreview = signing.savedSignature

  return (
    <div className={cn('space-y-4 rounded-md border p-4', className)}>
      <div className='flex items-center gap-2'>
        <ShieldCheck className='h-4 w-4 text-muted-foreground' />
        <h3 className='text-sm font-semibold'>
          Signature
          {signing.required ? <span className='ml-1 text-destructive'>*</span> : null}
        </h3>
      </div>

      {hasSaved && (
        <div
          className={cn(
            'flex flex-wrap items-center justify-between gap-3 rounded-md border p-3',
            useSaved ? 'border-primary bg-primary/5' : 'border-border'
          )}
        >
          <div className='flex items-center gap-3'>
            {savedPreview?.signatureImage ? (
              <img
                src={savedPreview.signatureImage}
                alt='Saved signature'
                className={cn('max-h-10 object-contain', SIGNATURE_DISPLAY_CLASS)}
              />
            ) : (
              <span
                className={cn('text-xl', SIGNATURE_DISPLAY_CLASS)}
                style={{ fontFamily: signatureFontStack(savedPreview?.typedFont) }}
              >
                {savedPreview?.typedText}
              </span>
            )}
            <span className='text-xs text-muted-foreground'>Your saved signature</span>
          </div>

          <div className='flex flex-wrap items-center gap-2'>
            {useSaved ? (
              <Button
                type='button'
                size='sm'
                variant={'outline'}
                disabled={disabled}
                onClick={() => setUseSaved(false)}
              >
                <Edit className='h-3.5 w-3.5' />
                Create new signature
              </Button>
            ) : (
              <Button type='button' size='sm' variant={'outline'} disabled={disabled} onClick={() => setUseSaved(true)}>
                Use saved signature
                <ArrowRight className='h-3.5 w-3.5' />
              </Button>
            )}
          </div>
        </div>
      )}

      {!useSaved && (
        <Tabs value={method} onValueChange={(value) => setMethod(value as SignatureMethod)}>
          <TabsList>
            {allowed.map((tab) => (
              <TabsTrigger key={tab.method} value={tab.method} disabled={disabled}>
                <tab.icon className='h-3.5 w-3.5' />
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {allowed.some((tab) => tab.method === SignatureMethod.DRAWN) && (
            <TabsContent value={SignatureMethod.DRAWN} className='pt-3'>
              <SignaturePad ref={padRef} disabled={disabled} onChange={setDrawnHasInk} />
            </TabsContent>
          )}

          {allowed.some((tab) => tab.method === SignatureMethod.TYPED) && (
            <TabsContent value={SignatureMethod.TYPED} className='pt-3'>
              <SignatureTyped
                value={typedText}
                font={typedFont}
                disabled={disabled}
                onChange={({ typedText: nextText, typedFont: nextFont }) => {
                  setTypedText(nextText)
                  setTypedFont(nextFont)
                }}
              />
            </TabsContent>
          )}

          {allowed.some((tab) => tab.method === SignatureMethod.UPLOADED) && (
            <TabsContent value={SignatureMethod.UPLOADED} className='pt-3'>
              <SignatureUpload value={uploaded} onChange={setUploaded} disabled={disabled} />
            </TabsContent>
          )}
        </Tabs>
      )}

      {!useSaved && (
        <div className='flex items-start gap-2'>
          <Checkbox
            id='save-signature'
            checked={saveAsProfile}
            disabled={disabled}
            onCheckedChange={(checked) => setSaveAsProfile(checked === true)}
          />
          <Label
            htmlFor='save-signature'
            className='text-sm cursor-pointer font-normal leading-snug text-muted-foreground'
          >
            Save this signature so I can reuse it next time
          </Label>
        </div>
      )}

      <div className='flex items-start gap-2 rounded-md bg-muted/40 p-3'>
        <Checkbox
          id='signature-consent'
          checked={consentAccepted}
          disabled={disabled}
          onCheckedChange={(checked) => setConsentAccepted(checked === true)}
          className='mt-0.5'
        />
        <Label htmlFor='signature-consent' className='text-sm cursor-pointer font-normal leading-snug'>
          {signing.consentText}
        </Label>
      </div>

      {signing.documentHash && (
        <p className='text-xs text-muted-foreground'>
          Signing seals version <span className='font-mono'>{signing.documentHash.slice(0, 12)}</span> of this document.
        </p>
      )}
    </div>
  )
}

export default SignatureCapture
